import { Calendar, Time, User } from '@carbon/icons-react';
import { Document } from '@contentful/rich-text-types';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { format as formatDate, formatDistanceToNow, parseISO as parseISODate } from 'date-fns';
import { useEffect } from 'react';

import {
  isTypeCategoryPage,
  isTypeProfile,
  TypeBlogPost,
  TypeBlogPostSkeleton,
  TypeCategoryPage,
  TypeProfile,
} from '../../../@types/generated';
import PageHero from '../../common-components/component-library/heroes/PageHero';
import CarouselLayout from '../../common-components/component-library/layout/carouselLayout/CarouselLayout';
import Container from '../../common-components/component-library/layout/Container';
import FormCTA from '../../common-components/component-library/layout/FormCTA';
import HighlightedProfilesLayout from '../../common-components/component-library/layout/highlightedProfilesLayout/HighlightedProfilesLayout';
import Typography from '../../common-components/component-library/text/Typography';
import { transformMetaHeaderType } from '../../common-components/contentful-elements/MetaHeader';
import RichText from '../../common-components/contentful-elements/RichText/RichText';
import Image from '../../common-components/component-library/ImageNext';
import ArticleLinkList from '../../domains/articles/ArticleLinkList';
import { getReadTime } from '../../domains/articles/articleUtils';
import { EVENT_NAMES, sendEvent } from '../../utils/analytics/analytics';
import { getByUniqueSlug, getEntries } from '../../utils/api/contentful';
import { breakpoints, colors, spacing } from '../../utils/styleguide';
import withAppProps from '../../utils/withAppProps';
import { EntryCollection } from 'contentful';
import { GetStaticPaths } from 'next';

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;
  background-color: ${colors.white};
  gap: ${spacing[5]}px;

  ${breakpoints.desktop} {
    padding: 0 ${spacing[5]}px;
  }
`;

const LinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]}px;
`;

const FlexBox = styled.div`
  display: flex;
  gap: ${spacing[3]}px;
`;

const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: ${spacing[9]}px;
  row-gap: ${spacing[3]}px;

  ${breakpoints.tablet} {
    flex-direction: row;
  }
`;

const HeaderImageContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ContentBox = styled.div`
  background-color: ${colors.white};
  position: relative;
  padding: ${spacing[1]}px ${spacing[3]}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1 0 0;
  align-self: stretch;
  gap: ${spacing[2]}px;

  ${breakpoints.desktop} {
    position: absolute;
    width: 300px;
    bottom: 0;
    right: 0;
    justify-content: flex-start;
  }
`;

type Article = {
  title?: string;
  preamble?: string;
  body?: Document;
  publishedAt?: string;
  updatedAt?: string;
  image?: {
    url?: string;
    title?: string;
    description?: string;
    width: number;
    height: number;
  };
  imageCredit?: string;
  readTime?: number;
  slug?: string;
  externalLink?: string;
  author?: string;
  authorProfiles?: {
    id: string;
    name: string;
    title: string;
    description?: Document;
    imageUrl?: string;
  }[];
  mentionedImpactFunds?: {
    id: string;
    title: string;
    slug?: string;
  }[];
};

export type Props = {
  mainArticle: Article;
  blogPosts: {
    title?: string;
    publishedAt: string;
    duration: number;
    url: string;
    externalLink?: string;
    image: {
      url?: string;
      alt: string;
      width: number;
      height: number;
    };
    label: string;
  }[];
};

function ArticlePage(props: Props) {
  const { mainArticle, blogPosts } = props;

  useEffect(() => {
    if (mainArticle.title && mainArticle.slug) {
      sendEvent({
        name: EVENT_NAMES.viewBlogPost,
        properties: {
          title: mainArticle.title,
          slug: mainArticle.slug,
        },
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    title,
    preamble,
    body,
    publishedAt,
    updatedAt,
    image,
    imageCredit,
    readTime,
    authorProfiles,
    mentionedImpactFunds,
  } = mainArticle;

  const hasLinks = !!mentionedImpactFunds?.length;

  return (
    <>
      <PageHero title={title ?? 'Article'} preamble={preamble} />
      <Container disableVerticalPadding>
        <ContentWrapper>
          <InfoWrapper>
            {!!authorProfiles?.length && (
              <FlexBox>
                <User size={24} />
                <ColumnWrapper>
                  {authorProfiles.map((profile) => (
                    <Typography key={profile.id} color={colors.blackSecondary}>
                      {profile.name}
                    </Typography>
                  ))}
                </ColumnWrapper>
              </FlexBox>
            )}
            {!!publishedAt && !!updatedAt && (
              <FlexBox>
                <Calendar size={24} />
                <ColumnWrapper>
                  <Typography color={colors.blackSecondary}>
                    {formatDate(parseISODate(publishedAt), 'MMM dd, yyyy')}
                  </Typography>
                  <Typography color={colors.blackSecondary} style={{ fontStyle: 'italic' }}>
                    {`Updated ${formatDistanceToNow(new Date(updatedAt))} ago`}
                  </Typography>
                </ColumnWrapper>
              </FlexBox>
            )}
            {!!readTime && (
              <FlexBox>
                <Time size={24} />
                <Typography color={colors.blackSecondary}>{readTime} min read</Typography>
              </FlexBox>
            )}
          </InfoWrapper>

          <HeaderImageContainer>
            {image?.url && (
              <Image
                src={image.url}
                alt={image.description ?? image.title ?? ''}
                width={image.width}
                height={image.height}
                priority
                css={css`
                  width: 100%;
                  height: auto;
                  max-height: 540px;
                  object-fit: cover;
                `}
                sizes="(max-width: 320px) 100vw, (max-width: 1200px) 100vw, 100vw"
              />
            )}

            {(image?.description || imageCredit) && (
              <ContentBox>
                {image?.description && (
                  <Typography variant="detail">{image.description}</Typography>
                )}
                {imageCredit && (
                  <Typography variant="detail">{imageCredit.toUpperCase()}</Typography>
                )}
              </ContentBox>
            )}
          </HeaderImageContainer>
        </ContentWrapper>
      </Container>

      <Container>
        <ContentWrapper>
          <RichText document={body} />

          {hasLinks && (
            <LinksWrapper>
              <Typography variant="overlineBold">Related links</Typography>

              {!!mentionedImpactFunds?.length && (
                <ArticleLinkList items={mentionedImpactFunds} linkPrefix="/impact-funds" />
              )}
            </LinksWrapper>
          )}
        </ContentWrapper>
      </Container>

      {!!authorProfiles?.length && (
        <HighlightedProfilesLayout
          sectionTitle="About the authors"
          primaryProfiles={authorProfiles}
        />
      )}

      <CarouselLayout sectionTitle="More Articles" items={blogPosts} />

      <FormCTA
        ctaFormSectionTitle="Contact us"
        ctaFormTitle="Ready to make real impact?"
        ctaFormSections={[
          {
            title: 'Get in touch',
            renderDescription: () => (
              <Typography>
                Discover how our innovative portfolios can elevate your commitment to global
                sustainability. Partner with us to make a lasting impact.
              </Typography>
            ),
            link: {
              title: 'Get in touch',
              url: '/contact',
            },
          },
          {
            title: 'Sign up for our newsletter',
            renderDescription: () => (
              <Typography>
                Join our newsletter and be the first to know about the exciting progress in our
                portfolios, expert insights, and the latest updates from our platform.
              </Typography>
            ),
            link: {
              title: 'Sign up',
              url: '/newsletter-signup',
            },
          },
        ]}
      />
    </>
  );
}

export const fieldsToProps = (page: TypeBlogPost) => {
  return {
    title: page.fields.title,
    preamble: page.fields.preamble,
    body: page.fields.body,
    slug: page.fields.slug,
    externalLink: page.fields.externalLink,
    createdAt: page.sys.createdAt,
    publishedAt: page.fields.publishedAt,
    updatedAt: page.sys.updatedAt,
    image: page.fields.image
      ? {
          url: page.fields.image.fields.file?.url,
          title: page.fields.image.fields.title,
          description: page.fields.image.fields.description,
          width: page.fields.image.fields.file?.details?.image?.width ?? 0,
          height: page.fields.image.fields.file?.details?.image?.height ?? 0,
        }
      : undefined,
    imageCredit: page.fields.imageCredit,
    readTime: getReadTime({
      body: page.fields.body?.content,
      title: page.fields.title,
      preamble: page.fields.preamble,
    }),
    author: page.fields.author,
    authorProfiles: page.fields.authorProfiles
      ?.filter((profile): profile is TypeProfile => !!profile && isTypeProfile(profile))
      .map((profile) => ({
        id: profile.sys.id,
        name: profile.fields.name,
        title: profile.fields.title,
        description: profile.fields.profileDescription,
        imageUrl: profile.fields.cutoutImage?.fields.file?.url,
      })),
    mentionedImpactFunds: page.fields.impactFunds
      ?.filter(
        (category): category is TypeCategoryPage =>
          !!category && isTypeCategoryPage(category) && Boolean(category?.fields?.slug?.length),
      )
      .map((category) => ({
        id: category.sys.id,
        title: category.fields.title,
        slug: category.fields.slug,
      })),
  };
};

export function getArticlePageProps(
  page: TypeBlogPost,
  blogPosts: EntryCollection<TypeBlogPostSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>,
  HOST_URL?: string,
) {
  const metaHeader = page.fields.metaHeader;
  const slug = page.fields.slug;

  return {
    props: {
      mainArticle: fieldsToProps(page),
      blogPosts: blogPosts.items.map((article) => ({
        title: article.fields.title,
        publishedAt: article.fields.publishedAt,
        duration: getReadTime({
          body: article.fields.body?.content,
          title: article.fields.title,
          preamble: article.fields.preamble,
        }),
        url: `/articles/${article.fields.slug}`,
        externalLink: article.fields.externalLink,
        image: {
          url: article.fields.image?.fields.file?.url,
          alt: article.fields.image?.fields.description ?? '',
          height: article.fields.image?.fields.file?.details?.image?.height ?? 0,
          width: article.fields.image?.fields.file?.details?.image?.width ?? 0,
        },
        label: 'Read more',
      })),
    },
    pageAppProps: {
      metaHeader: transformMetaHeaderType({
        metaHeader,
        fallbacks: {
          seoTitle: `${page.fields.title} | Milkywire`,
          seoDescription: page.fields.preamble,
          socialTitle: `${page.fields.title} | Milkywire`,
          socialDescription: page.fields.preamble,
          socialImageUrl: page.fields.image?.fields.file?.url,
          canonicalLink: `${HOST_URL}/articles/${slug}`,
        },
      }),
    },
  };
}

export const getStaticPaths: GetStaticPaths = async () => {
  // Generate pages on demand instead of at build time to avoid environment issues
  return { paths: [], fallback: 'blocking' };
};

export const getStaticProps = withAppProps<Props>(async (context, config) => {
  const slug = context.params?.slug as string;

  const [page, blogPosts] = await Promise.all([
    getByUniqueSlug<TypeBlogPost>({
      slug,
      contentType: 'blogPost',
    }),
    // We might want to look at loading these in the client instead to avoid revalidation
    getEntries<TypeBlogPostSkeleton>({
      content_type: 'blogPost',
      'fields.tag': 'Article',
      'fields.availableFor': 'Main web',
      order: ['-fields.publishedAt'],
      'fields.slug[ne]': slug,
      limit: 6,
    }),
  ]);

  const props = getArticlePageProps(page, blogPosts, config.HOST_URL);

  return {
    ...props,
    revalidate: 1800, // 30 minutes
  };
});

export default ArticlePage;
